/**
 * Shared certifications list used by both EMT onboarding and Employer job posting.
 * This ensures consistency across the platform.
 *
 * Four categories (see CERT_CATEGORY below):
 * - registration:   NREMT-B/I/P — national exam-based registration
 * - training_level: EMT-B/AEMT/Paramedic — state scope-of-practice license level
 * - state_certification: State EMS Certification — issued by one state's EMS office; a profile
 *                   entry carries the issuing state (`state`, 2-letter code)
 * - skills_cert:    BLS/ACLS/PALS — AHA card-based skills certs
 * - driving_cert:   EVOC (Emergency Vehicle Operator Course), plus the California Ambulance Driver
 *                   Certificate, which only California issues (offered for CA jobs / CA EMTs only,
 *                   see credentialOptionsForState). There is no generic "Ambulance Driver's License".
 *
 * Registration and training-level are tracked as independent, separately selectable
 * certs (a candidate can hold either without the other — true in non-NREMT states),
 * but for job-requirement MATCHING they remain equivalent via CERT_MATCH_GROUPS below,
 * preserving the pre-existing EMT-B ≡ NREMT-B behavior every existing job posting relies on.
 */
export const STATE_EMS_CERTIFICATION = 'State EMS Certification';
export const CA_AMBULANCE_DRIVER_CERTIFICATE = 'California Ambulance Driver Certificate';

export const EMT_CERTIFICATIONS = [
  'NREMT-B',
  'NREMT-I',
  'NREMT-P',
  'EMT-B',
  'AEMT',
  'Paramedic',
  'State EMS Certification',
  'BLS',
  'ACLS',
  'PALS',
  'Critical Care Paramedic Certificate',
  'Wilderness First Responder',
  'Wilderness EMT',
  'EVOC',
  'California Ambulance Driver Certificate',
] as const;

/**
 * EMR (Emergency Medical Responder), the level below EMT-Basic on the EMS ladder (2026-10-09, flag
 * `driver_roles`). It is a real credential everywhere it is stored (resolution, the ladder, display,
 * matching on client and server), but it is deliberately NOT in EMT_CERTIFICATIONS: every list built
 * from EMT_CERTIFICATIONS (institute programs, employer search, resume tools) stays exactly as before.
 * Pickers offer it only while `driver_roles` is on (credentialOptionsForState({ driverRoles: true })).
 */
export const EMR_CERTIFICATION = 'EMR' as const;

export type Certification = (typeof EMT_CERTIFICATIONS)[number] | typeof EMR_CERTIFICATION;

/** Everything resolvable: the catalog plus EMR, EMR placed just before EMT-B (the order pickers show). */
export const ALL_CERTIFICATIONS: readonly Certification[] = (() => {
  const list: Certification[] = [...EMT_CERTIFICATIONS];
  list.splice(list.indexOf('EMT-B'), 0, EMR_CERTIFICATION);
  return list;
})();

export type CertCategory = 'registration' | 'training_level' | 'state_certification' | 'skills_cert' | 'driving_cert';

export const CERT_CATEGORY: Record<Certification, CertCategory> = {
  'NREMT-B': 'registration',
  'NREMT-I': 'registration',
  'NREMT-P': 'registration',
  EMR: 'training_level',
  'EMT-B': 'training_level',
  AEMT: 'training_level',
  Paramedic: 'training_level',
  'State EMS Certification': 'state_certification',
  BLS: 'skills_cert',
  ACLS: 'skills_cert',
  PALS: 'skills_cert',
  'Critical Care Paramedic Certificate': 'skills_cert',
  'Wilderness First Responder': 'skills_cert',
  'Wilderness EMT': 'skills_cert',
  EVOC: 'driving_cert',
  'California Ambulance Driver Certificate': 'driving_cert',
};

export const CERT_CATEGORY_LABELS: Record<CertCategory, string> = {
  registration: 'National Registration (NREMT)',
  training_level: 'Training / Scope-of-Practice Level',
  state_certification: 'State EMS Certification',
  skills_cert: 'Skills Certifications',
  driving_cert: 'Driving Certification',
};

/** Display order for category sections in grouped UIs. */
export const CERT_CATEGORY_ORDER: CertCategory[] = [
  'registration',
  'training_level',
  'state_certification',
  'skills_cert',
  'driving_cert',
];

/**
 * Credentials only one state issues, keyed to that state's 2-letter code. They are offered in a
 * picker only for a job in that state or an EMT whose address is in that state
 * (credentialOptionsForState). An entry already on a profile or a job still resolves and displays
 * everywhere; this only limits what is OFFERED.
 */
export const STATE_LIMITED_CERTIFICATIONS: Partial<Record<Certification, string>> = {
  // California DMV issues the Ambulance Driver Certificate (form DL 61):
  // https://www.dmv.ca.gov/portal/driver-education-and-safety/dmv-safety-guidelines-actions/driver-license-certificates-and-endorsements/
  'California Ambulance Driver Certificate': 'CA',
};

/** A 2-letter state code, upper-cased, or null when the value is not one. */
export function normalizeStateCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(s) ? s : null;
}

/**
 * The credentials a picker should offer for a job in `stateCode` (employer side) or an EMT whose
 * address is in `stateCode` (EMT side). State-limited credentials (California Ambulance Driver
 * Certificate) appear only for their state. `alsoInclude` keeps anything already selected visible,
 * so an existing choice never silently disappears from the form when the state changes.
 */
export function credentialOptionsForState(
  stateCode: string | null | undefined,
  alsoInclude: readonly string[] = [],
  opts: { driverRoles?: boolean } = {},
): Certification[] {
  const state = normalizeStateCode(stateCode);
  const keep = new Set<Certification>();
  for (const name of alsoInclude) {
    const canonical = resolveCanonicalCertification(name);
    if (canonical) keep.add(canonical);
  }
  return ALL_CERTIFICATIONS.filter((c) => {
    // EMR is offered only while `driver_roles` is on (or when already selected, so it can be removed).
    if (c === EMR_CERTIFICATION && !opts.driverRoles && !keep.has(c)) return false;
    const only = STATE_LIMITED_CERTIFICATIONS[c];
    return !only || only === state || keep.has(c);
  });
}

/**
 * Certs in the same match group satisfy one another for job-requirement matching,
 * even though they're now independently selectable on the profile. Registration and
 * training-level pairs (NREMT-B/EMT-B, NREMT-I/AEMT, NREMT-P/Paramedic) share a group
 * so a job requiring "EMT-B" is still satisfied by a candidate who only holds NREMT-B,
 * and vice versa — this is the pre-existing alias-matching behavior, now preserved
 * explicitly instead of via a lossy alias collapse. Do not remove without auditing
 * existing job postings' required-certification matching.
 */
const CERT_MATCH_GROUPS: Partial<Record<Certification, string>> = {
  EMR: 'match:emr',
  'NREMT-B': 'match:emt-b',
  'EMT-B': 'match:emt-b',
  'NREMT-I': 'match:aemt',
  AEMT: 'match:aemt',
  'NREMT-P': 'match:paramedic',
  Paramedic: 'match:paramedic',
};

function certGroupKey(canonical: Certification): string {
  return CERT_MATCH_GROUPS[canonical] ?? normalizeCertKey(canonical);
}

/**
 * Typical validity in years for known cert types — shown as a hint beside expiry fields, never
 * used to fill one in (see certificationExpiryHint). NREMT Mar 31 / EMR Sep 30 cycle pinning is
 * not modeled.
 *
 * NREMT-I is treated as the AEMT-equivalent 2-year cycle. EMT-B/AEMT/Paramedic (state license
 * levels) have NO entry (2026-10-10 verification sweep): state cycles differ (New York 4 years,
 * Illinois 4 years under 77 IAC 515.540(c), Colorado 3 years), so the hint falls back to "enter the
 * date printed on the document" instead of guessing 2 years.
 *
 * EVOC intentionally has NO entry: no uniform national renewal cycle exists (varies
 * 2-4 years by state/regional EMS system/employer per research; some jurisdictions
 * use in-house refreshers with no fixed expiration). certificationExpiryHint()
 * returns null for certs without an entry here — no silent guess.
 */
export const CERT_VALIDITY_YEARS: Partial<Record<Certification, number>> = {
  // NREMT recertifies Emergency Medical Responders on the same 2-year cycle as EMTs.
  EMR: 2,
  'NREMT-B': 2,
  'NREMT-I': 2,
  'NREMT-P': 2,
  ACLS: 2,
  PALS: 2,
  BLS: 2,
};

/** Collapse punctuation/spaces for alias matching. */
export function normalizeCertKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s_]+/g, '-').replace(/-+/g, '-');
}

const CERT_ALIASES: Record<string, Certification> = {
  // EMR: NREMT registers Emergency Medical Responders at this level too ("NREMT-EMR"), same credential.
  emr: 'EMR',
  'nremt-emr': 'EMR',
  nremtemr: 'EMR',
  'emergency-medical-responder': 'EMR',
  'nremt-b': 'NREMT-B',
  nremtb: 'NREMT-B',
  'nremt-emt': 'NREMT-B',
  'emt-basic': 'EMT-B',
  'emt-b-license': 'EMT-B',
  'nremt-i': 'NREMT-I',
  nremti: 'NREMT-I',
  'nremt-a': 'NREMT-I',
  nremta: 'NREMT-I',
  'emt-i': 'AEMT',
  'emt-intermediate': 'AEMT',
  'advanced-emt': 'AEMT',
  'nremt-p': 'NREMT-P',
  nremtp: 'NREMT-P',
  'emt-p': 'Paramedic',
  'emt-paramedic': 'Paramedic',
  acls: 'ACLS',
  pals: 'PALS',
  // The IBSC exam is the CCP-C; employers write it several ways (2026-11-01, Trident's job posting).
  'ccp-c': 'Critical Care Paramedic Certificate',
  ccp: 'Critical Care Paramedic Certificate',
  'critical-care-paramedic': 'Critical Care Paramedic Certificate',
  'critical-care-paramedic-certification': 'Critical Care Paramedic Certificate',
  'certified-critical-care-paramedic': 'Critical Care Paramedic Certificate',
  wfr: 'Wilderness First Responder',
  'wilderness-first-responder-wfr': 'Wilderness First Responder',
  wemt: 'Wilderness EMT',
  'w-emt': 'Wilderness EMT',
  'wilderness-emt-wemt': 'Wilderness EMT',
  'wilderness-emergency-medical-technician': 'Wilderness EMT',
  bls: 'BLS',
  cpr: 'BLS',
  'bls-cpr': 'BLS',
  'cpr-bls': 'BLS',
  'aha-bls': 'BLS',
  'aha-bls-cpr': 'BLS',
  'aha-cpr': 'BLS',
  'basic-life-support': 'BLS',
  evoc: 'EVOC',
  'emergency-vehicle-operations': 'EVOC',
  'emergency-vehicle-operations-course': 'EVOC',
  'emergency-vehicle-operator-course': 'EVOC',
  'state-ems-certification': 'State EMS Certification',
  'state-ems-certificate': 'State EMS Certification',
  'state-ems-cert': 'State EMS Certification',
  'state-ems-license': 'State EMS Certification',
  'state-emt-license': 'State EMS Certification',
  'state-emt-certification': 'State EMS Certification',
  'ca-adc': 'California Ambulance Driver Certificate',
  'california-ambulance-driver-certificate': 'California Ambulance Driver Certificate',
  'ca-ambulance-driver-certificate': 'California Ambulance Driver Certificate',
  'ambulance-driver-certificate': 'California Ambulance Driver Certificate',
};

/**
 * Map free-text / alias input onto a canonical EMT_CERTIFICATIONS value when possible.
 * Exact list match first, then case-insensitive aliases (e.g. "EMT-B" → "NREMT-B").
 */
export function resolveCanonicalCertification(raw: string): Certification | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const exact = ALL_CERTIFICATIONS.find((c) => c === trimmed);
  if (exact) return exact;

  const key = normalizeCertKey(trimmed);
  const byNormalized = ALL_CERTIFICATIONS.find((c) => normalizeCertKey(c) === key);
  if (byNormalized) return byNormalized;

  return CERT_ALIASES[key] ?? CERT_ALIASES[key.replace(/-/g, '')] ?? null;
}

/**
 * Stable match key for eligibility / gap panels.
 * Canonical certs collapse to their match group (EMT-B ≡ NREMT-B); unknown strings
 * keep normalized form.
 */
export function certificationMatchKey(raw: string): string {
  const canonical = resolveCanonicalCertification(raw);
  return canonical ? certGroupKey(canonical) : normalizeCertKey(raw);
}

/** True when two cert labels refer to the same credential (alias-aware). */
export function certificationsEquivalent(a: string, b: string): boolean {
  if (!a?.trim() || !b?.trim()) return false;
  return certificationMatchKey(a) === certificationMatchKey(b);
}

/**
 * EMS scope-of-practice ladder. A higher level includes everything below it: a Paramedic is
 * trained and licensed beyond AEMT and EMT-B, an AEMT beyond EMT-B, and an EMT-B beyond EMR
 * (Emergency Medical Responder, added 2026-10-09), so a requirement for a lower level is met by
 * holding a higher one. EMR never meets an EMT-B requirement. Skills cards (BLS, ACLS, PALS, EVOC)
 * are NOT on the ladder: holding ACLS says nothing about BLS paperwork, so they only ever match
 * themselves. Only the ORDER of the ranks matters (every comparison is greater-than / at-least).
 */
const CERT_LEVEL_RANK: Record<string, number> = {
  'match:emr': 1,
  'match:emt-b': 2,
  'match:aemt': 3,
  'match:paramedic': 4,
};

/**
 * True when holding `held` satisfies a requirement for `required`: the same credential
 * (alias-aware, EMT-B ≡ NREMT-B) or a HIGHER level on the EMS ladder. Asymmetric by design —
 * an EMT-B never satisfies a Paramedic requirement. This is the single rule behind job
 * matching, the hiring-checklist comparison and the "Documents Reviewed" apply gate.
 */
export function certificationSatisfies(held: string, required: string): boolean {
  if (!held?.trim() || !required?.trim()) return false;
  const heldKey = certificationMatchKey(held);
  const reqKey = certificationMatchKey(required);
  if (!heldKey || !reqKey) return false;
  if (heldKey === reqKey) return true;
  const heldRank = CERT_LEVEL_RANK[heldKey];
  const reqRank = CERT_LEVEL_RANK[reqKey];
  return heldRank !== undefined && reqRank !== undefined && heldRank > reqRank;
}

/** Whether the profile cert list satisfies a single required label (same credential or a higher level). */
export function profileHasCertification(
  profileCertNames: readonly string[],
  required: string,
): boolean {
  if (!certificationMatchKey(required)) return false;
  return profileCertNames.some((c) => certificationSatisfies(c, required));
}

/**
 * Prefer canonical display for known certs; otherwise keep the original label.
 * Used when reporting missing requirements so apply gate and gap panel agree.
 */
export function displayCertificationName(raw: string): string {
  return resolveCanonicalCertification(raw) ?? raw.trim();
}

/** "State EMS Certification (FL)" for a state certification with its state; otherwise the display name. */
export function formatCertificationName(name: string, state?: string | null, level?: string | null): string {
  const display = displayCertificationName(name);
  if (display !== STATE_EMS_CERTIFICATION) return display;
  const parts = [normalizeStateCode(state), normalizeStateCertificationLevel(level)].filter(Boolean);
  return parts.length ? `${display} (${parts.join(', ')})` : display;
}

/** A level on the EMS ladder, as a state rule names it. */
export type EmsRuleLevel = 'EMR' | 'EMT-B' | 'AEMT' | 'Paramedic';

/**
 * One state's National Registry (NREMT) rule, read off an official page (2026-10-10, Part A4).
 *
 * What matters for HIRING is not how someone first got licensed but whether the state requires a
 * CURRENT NREMT certification to KEEP (renew) the state license. Florida, for example, requires
 * passing the NREMT exam for a NEW license (its state exam closed 8/1/2018) but does not require
 * keeping NREMT current to renew, so a Florida EMT whose NREMT has lapsed is still a licensed
 * Florida EMT.
 *
 *  - initialWithoutNremt: can a NEW state license be had without passing NREMT (null = not stated).
 *  - renewalRequiresNremt: does keeping the license require a current NREMT. A level listed in
 *    renewalRequiresNremtForLevels overrides it for that level only (Georgia: EMT-Responder).
 *  - source / checked: the official page the rule was confirmed on, and when.
 *
 * A state is listed ONLY with a confirmed official source. Do not add a state from memory.
 *
 * NOT CONFIRMED (founder rule, 2026-10-14): a state or a level with no confirmed official answer (not
 * listed, or null below) is treated as NOT requiring a current NREMT, so an EMT there is never marked
 * down for not holding one. Only a CONFIRMED "required" (SC, UT, AL, AR, DC, ...) withholds the
 * stand-in. The factual sentence "{State} doesn't require a current NREMT to keep it" is shown only
 * for a confirmed "not required"; an unconfirmed state gets a neutral sentence (stateCertificationNote).
 *
 * WATCH: North Carolina bill H675 would require NREMT. Not passed as of Oct 2026; if it passes,
 * NC's entry below must change (https://www.ncleg.gov/BillLookUp/2025/H675).
 */
export type StateNremtRule = {
  initialWithoutNremt: { emt: boolean | null; paramedic: boolean | null };
  renewalRequiresNremt: boolean;
  /** Per-level exception. null = not confirmed for that level (not stated, or official sources disagree). */
  renewalRequiresNremtForLevels?: Partial<Record<EmsRuleLevel, boolean | null>>;
  source: string;
  checked: string;
  note: string;
};

// Research notes with word-for-word quotes for every row: docs/research-2026-10-10/nremt-states.md.
export const STATE_NREMT_RULES: Readonly<Record<string, StateNremtRule>> = {
  // Current NREMT NOT required to keep the license.
  FL: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    source: 'https://www.floridahealth.gov/licensing-regulations/regulated-professions/emergency-medical-technicians-and-paramedics/',
    checked: '2026-10-10',
    note: 'New licenses need the NREMT exam (paramedics since August 1, 2018); renewal is 30 hours of continuing education to the state.',
  },
  NY: {
    initialWithoutNremt: { emt: true, paramedic: true },
    renewalRequiresNremt: false,
    // Policy 25-06 (recertification by NYS CME) covers CFR, EMT and AEMT only; no official page found
    // for paramedic recertification, so a Paramedic-level NY certification is treated as unknown.
    renewalRequiresNremtForLevels: { Paramedic: null },
    source: 'https://healthweb-back.health.ny.gov/professionals/ems/pdf/25-06.pdf',
    checked: '2026-10-10',
    note: 'Policy 24-06: a state exam or the NREMT exam. Policy 25-06: CFR, EMT and AEMT recertify through the NYS CME program.',
  },
  IL: {
    // Official sources disagree on first licenses (IDPH testing page offers a state exam; 77 IAC
    // 515.530 says candidates take the NREMT cognitive exam), so the first-license answer is unknown.
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.ilga.gov/agencies/JCAR/EntirePart?titlepart=07700515',
    checked: '2026-10-10',
    note: '77 IAC 515.590 and the IDPH renewal brochure: renewal is continuing education, no NREMT.',
  },
  NC: {
    // WATCH H675 (https://www.ncleg.gov/BillLookUp/2025/H675): would require NREMT for credentialing and
    // renewal. In the House Rules committee since 4/29/2025; NOT passed as of Oct 2026.
    initialWithoutNremt: { emt: true, paramedic: true },
    renewalRequiresNremt: false,
    source: 'https://oems.nc.gov/education-faqs/',
    checked: '2026-10-10',
    note: 'The National Registry credential is one way to renew, not a requirement.',
  },
  MI: {
    // MCL 333.20954 allows NREMT "or another organization approved by the department": not stated.
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.legislature.mi.gov/Laws/MCL?objectName=mcl-333-20954',
    checked: '2026-10-10',
    note: 'MCL 333.20954: renewal does not require national registry status (NREMT exams must be passed once).',
  },
  TX: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    source: 'https://dshs.texas.gov/dshs-ems-trauma-systems/ems-personnel-certification-licensure/renewals-ems-responder-certification',
    checked: '2026-10-10',
    note: 'Current National Registry certification is one of several renewal options.',
  },
  CA: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    // emsa.ca.gov web pages were behind a login on 2026-10-10; confirmed from EMSA's own regulations
    // (22 CCR 100069.01 and 100095, effective Jan 1, 2025) and the live renewal form RL-01.
    source: 'https://emsa.ca.gov/wp-content/uploads/sites/71/2024/10/California-California-EMS-Authority-Regulations-effective-January-1-2025.pdf',
    checked: '2026-10-10',
    note: 'EMSA: not required to maintain NREMT to renew (EMT) or for continued licensure (paramedic).',
  },
  GA: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    // EMT-Responder (EMR) must keep current NREMT (511-9-2-.13(2)). Critical Care Paramedics too (DPH
    // Policy OEMS-CCP-2025-001); that endorsement is not modeled, so it has no level here.
    renewalRequiresNremtForLevels: { EMR: true },
    source: 'https://rules.sos.ga.gov/gac/511-9-2',
    checked: '2026-10-10',
    note: '511-9-2-.13: EMT, AEMT and Paramedic renew with continuing education; EMT-Responders keep current NREMT.',
  },
  CO: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    source: 'https://cdphe.colorado.gov/emergency-care/emergency-medical-services/ems-providers/ems-provider-certification',
    checked: '2026-10-10',
    note: 'Providers not registered with NREMT can renew with the state renewal method.',
  },
  // Current NREMT REQUIRED to keep the license: a state certification never stands in for NREMT here.
  SC: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: true,
    // Regulation 61-7 is now numbered 60-7 (SC DPH, amended May 23, 2025), section 107.
    source: 'https://dph.sc.gov/sites/scdph/files/Library/Regulations/R.60-7.pdf',
    checked: '2026-10-10',
    note: 'Section 107: renewal needs documentation of current NREMT credentials at the level.',
  },
  UT: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: true,
    // R911-5-204(1)(d) and R911-5-400. Official text: "Date of Last Change: June 10, 2025".
    source: 'https://adminrules.utah.gov/api/public/getHTML/uac-html/35384c59-9d86-4d39-8bd4-c8737da1c67c.html',
    checked: '2026-10-10',
    note: 'Licensees must keep NREMT certification; renewal needs a current NREMT certification.',
  },
  // ---- 2026-10-14: every other state and DC, each from its official EMS rule or office page opened on
  // 2026-10-13 (claims register rows 2.12 to 2.51; word-for-word quotes in docs/build-2026-10-14-insights).
  // A level the official text does not state, or where official sources disagree, is null: not confirmed.
  AK: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { EMR: null },
    source: 'https://www.akleg.gov/basis/aac.asp?media=print&secStart=7.26.010&secEnd=7.26.999',
    checked: '2026-10-13',
    note: '7 AAC 26.110(a)(3): "for a department-approved corresponding level; or (B) successful completion of department-approved continuing medical education"',
  },
  AL: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: true,
    source: 'https://admincode.legislature.state.al.us/api/rule/420-2-1-.10',
    checked: '2026-10-13',
    note: 'Ala. Admin. Code r. 420-2-1-.10(10)(a)-(e); initial: r. 420-2-1-.08(2)(a): "2. OEMS approved adult and pediatric protocols update. 3. Current NREMT certification."',
  },
  AR: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: true,
    source: 'https://healthy.arkansas.gov/wp-content/uploads/EMS-Rules_.pdf',
    checked: '2026-10-13',
    note: 'Arkansas Rules for Emergency Medical Services (effective Oct 28, 2024), Section VIII.D.2.a-d; see also VIII.E.2: "2) Renewal of NREMT certification is required."',
  },
  AZ: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { EMR: null },
    source: 'https://www.azdhs.gov/documents/preparedness/emergency-medical-services-trauma-system/nofollow/adhs-emct-cert-and-recertification-requirements.pdf',
    checked: '2026-10-13',
    note: '9 A.A.C. 25, Article 4 (R9-25-404), as revised effective Dec 31, 2024: "Prerequisite Current ADHS Certification ... 24 Hours of CE or Refresher course"',
  },
  CT: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://portal.ct.gov/dph/emergency-medical-services/licensing--certificaton/emt-renewalrecertification',
    checked: '2026-10-13',
    note: 'CT DPH OEMS 2025 EMS Recertification Requirements (effective July 1, 2025): "Complete 40 hours of continued education for an EMT as required by NREMT; OR Be currently certified by the NREMT as an EMT."',
  },
  DC: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: true,
    source: 'https://dcregs.dc.gov/Common/DCMR/RuleList.aspx?DownloadFile=3056555F-0000-C633-A356-57624996B6C6',
    checked: '2026-10-13',
    note: '29 DCMR 515.16; initial 29 DCMR 515.5 and 517.1(a): "shall maintain a valid NREMT certification card at the same or greater provider level as his or her DC certification"',
  },
  DE: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { 'EMT-B': null, AEMT: null, Paramedic: true },
    source: 'https://regulations.delaware.gov/api/AdminCode/title1/710/f39d634e-ca16-42df-88dd-df508c9512c2',
    checked: '2026-10-13',
    note: '1 DE Admin. Code 710-18.2.1 (EMT); 710-22.2.3 and 22.4 (EMR); 24 DE Admin. Code 1700-14.2.1.1 (Paramedic): "All EMTs must maintain their NREMT card to maintain a Delaware EMT certification."',
  },
  HI: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { EMR: null, AEMT: null },
    source: 'https://cca.hawaii.gov/wp-content/uploads/2026/02/har_85-c2-1-1.pdf',
    checked: '2026-10-13',
    note: 'HAR §16-85-55(a)-(b); initial HAR §16-85-54(b)(1)(D)-(E), (2): "Certificate holders who do not have a current certificate from the NREMT shall submit evidence of continuing education (CE) requirements"',
  },
  IA: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    source: 'https://www.legis.iowa.gov/docs/iac/chapter/641.131.pdf',
    checked: '2026-10-13',
    note: 'Iowa Admin. Code 641-131.5(7)(a),(c); initial 641-131.2(1): "An emergency medical care provider who is registered with the NREMT may renew the provider\'s Iowa EMS certification by meeting the NREMT\'s requirements."',
  },
  ID: {
    initialWithoutNremt: { emt: true, paramedic: true },
    renewalRequiresNremt: false,
    source: 'https://ems.idaho.gov/personnel/',
    checked: '2026-10-13',
    note: 'IDAPA 16.01.07 (section not verified): "Options for reporting continuing education: CE hours reported in IGEMS; NREMT renewed within the last 2 years"',
  },
  IN: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://iar.iga.in.gov/code/current/836/4',
    checked: '2026-10-13',
    note: '836 IAC 4-3-2(c) EMR; 836 IAC 4-4-2(d) EMT; 836 IAC 4-7-5 AEMT; 836 IAC 4-9-5 Paramedic: "To renew a certification, a certified emergency medical technician shall submit a report of continuing education every two (2) years"',
  },
  KS: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.ksbems.org/html%20pages/KAR%20109%20EMS.pdf',
    checked: '2026-10-13',
    note: 'K.A.R. 109-5-1a to 109-5-1d; K.A.R. 109-6-2; K.S.A. 65-6129(d): "Have earned at least 28 clock-hours of board-approved continuing education during the initial certification period and during each biennial period thereafter"',
  },
  KY: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    source: 'https://apps.legislature.ky.gov/law/kar/titles/202/007/301/',
    checked: '2026-10-13',
    note: '202 KAR 7:201 Sec. 3 (EMR); 202 KAR 7:301 Sec. 3(1)(d) (EMT); 202 KAR 7:330 Sec. 3(2) (AEMT); 202 KAR 7:401 Sec. 3(1)(d) (Paramedic): "or 2. Successful completion of the NREMT Emergency Medical Technician National Component of the Continued Competency Program for Continuing Education"',
  },
  LA: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { 'EMT-B': true, AEMT: true, Paramedic: true },
    source: 'https://ldh.la.gov/assets/oph/ems/2026/EMS-COMMISSION/Act-483-BEMS-Final-2-1.0.pdf',
    checked: '2026-10-13',
    note: 'LDH Bureau of EMS, Act 483 report (January 2026), Subsection 1.2 Renewal License; licensing authority L.R.S. 40:1231 (per LDH FAQ): "Renew National Registry certification (Emergency Medical Responder optional)"',
  },
  MA: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { EMR: null, 'EMT-B': true, AEMT: true, Paramedic: true },
    source: 'https://www.mass.gov/doc/105-cmr-170-emergency-medical-services-system/download',
    checked: '2026-10-13',
    note: '105 CMR 170.930(A)(2)(a) (regulation page dated 09/29/2023): "EMTs who are currently NREMT certified must maintain documentation of current NREMT certification"',
  },
  MD: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { AEMT: null, Paramedic: true },
    source: 'https://regs.maryland.gov/us/md/exec/comar/30.02.02.07',
    checked: '2026-10-13',
    note: 'COMAR 30.02.02.07 D (EMR), E (EMT), F (CRT), G (Paramedic), for credentials expiring after July 31, 2025: "Current active status registration as a paramedic with the National Registry of Emergency Medical Technicians."',
  },
  ME: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.maine.gov/ems/licensing/renewal',
    checked: '2026-10-13',
    note: 'none on page (Maine EMS renewal page and CEH FAQ): "Maine EMS does not require CPR, ACLS, PHTLS, PALS or NREMT for renewal."',
  },
  MN: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.revisor.mn.gov/statutes/cite/144E.28',
    checked: '2026-10-13',
    note: 'Minn. Stat. 144E.28 subd. 7 (EMT, AEMT, Paramedic); 144E.27 subd. 3 (EMR): "successfully complete 48 hours of continuing education in emergency medical training programs, appropriate to the level of the applicant\'s AEMT or paramedic certification"',
  },
  MO: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { EMR: null },
    source: 'https://health.mo.gov/providers/emergency-medical-services/emt-and-paramedic-licensing-information',
    checked: '2026-10-13',
    note: 'page cites no rule; relicensure rule reported as 19 CSR 30-40.342 (official text not opened): "proof current certification as a Nationally Registered EMT Basic or proof of 100 hours of continuing education"',
  },
  MS: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: true,
    source: 'https://msdh.ms.gov/page/resources/8155.pdf',
    checked: '2026-10-13',
    note: 'Miss. Admin. Code Title 15, Part 12, Subpart 31: Rules 4.9.1(7) EMR, 6.9.1(7) EMT, 7.10.1(8) AEMT, 8.9.1(8) Paramedic (grounds for suspension or revocation): "Failure to maintain current registration by the National Registry of EMTs."',
  },
  MT: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://dli.mt.gov/_docs/rules/MAR-NOTICE-NO-2025-149.1pro-arm.pdf',
    checked: '2026-10-13',
    note: 'ARM 24.156.2718 (proposed to move to 24.112.2101); initial ARM 24.156.2711: "a certificate of completion, issued and signed by the lead instructor and/or medical director, of the NREMT continued competency training requirements"',
  },
  ND: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: true,
    source: 'https://ndlegis.gov/prod/acdata/pdf/33-36-01.pdf',
    checked: '2026-10-13',
    note: 'N.D. Admin. Code 33-36-01-03(1)(e), (2)(e), (3)(e), (4)(e) (amended eff. April 1, 2024): "An individual that maintains certification from the national registry as an emergency medical technician is eligible for relicensure."',
  },
  NE: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://dhhs.ne.gov/Pages/EHS-EMS-Licensing.aspx',
    checked: '2026-10-13',
    note: '172 NAC 11-007 (cited on page; rule text not opened): "must meet the standards of the national component of the National Continuing Competency Program\'s continued competency requirements or be Nationally Registered"',
  },
  NH: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: true,
    source: 'https://gc.nh.gov/rules/state_agencies/saf-c5900.html',
    checked: '2026-10-13',
    note: 'N.H. Admin. R. Saf-C 5904.06(d) (eff. 7-23-25), levels in Saf-C 5904.01(b)-(f) excluding (c): "Providers shall maintain their certification with the NHREMT in order to remain licensed at any of the licensing levels"',
  },
  NJ: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { EMR: null, AEMT: null },
    source: 'https://www.nj.gov/health/ems/documents/reg-enforcement/njac840ar.pdf',
    checked: '2026-10-13',
    note: 'N.J.A.C. 8:40A-7.5 (EMT-Basic); N.J.A.C. 8:41A-4.3 (EMT-Paramedic): "Successful completion of approved continuing education units, consisting of 24 core credit hours and 24 elective credit hours."',
  },
  NM: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://nmhealth.org/publication/view/guide/1894',
    checked: '2026-10-13',
    note: '7.27.2.11 NMAC (Licensure Renewal), https://www.srca.nm.gov/parts/title07/07.027.0002.html: "NOT required to maintain your National Registry of EMTs (NREMT) certification as a requirement for a New Mexico EMT license or license renewal."',
  },
  NV: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.leg.state.nv.us/NAC/NAC-450B.html',
    checked: '2026-10-13',
    note: 'NAC 450B.366 (EMR, 12 hrs); NAC 450B.380(4) (EMT 24 hrs, AEMT 30 hrs, Paramedic 40 hrs): "Successfully complete courses in continuing training which are equivalent to the national standard prepared by the National Highway Traffic Safety Administration"',
  },
  OH: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://codes.ohio.gov/ohio-administrative-code/rule-4765-8-04',
    checked: '2026-10-13',
    note: 'Ohio Adm. Code 4765-8-04(A)(3) (eff. Sept 1, 2025): "Satisfy one of the following: (a) Completion of continuing education requirements as provided under section 4765.24 of the Revised Code"',
  },
  OK: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    renewalRequiresNremtForLevels: { 'EMT-B': true, AEMT: true, Paramedic: true },
    source: 'https://oklahoma.gov/content/dam/ok/en/health/health2/aem-documents/protective-health/emergency-systems/ems-personnel-licensure/2024%20-%209-25-2023%20Option%202%20Individual%20Renewal.pdf',
    checked: '2026-10-13',
    note: 'OAC 310:641 (rule text not opened on an official site; quote is from the OSDH 2024 renewal packet, memo dated Sept 25, 2023): "(Personnel licensed after April 1, 2010 are required to maintain NREMT certification.)"',
  },
  OR: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://secure.sos.state.or.us/oard/view.action?ruleNumber=333-265-0110',
    checked: '2026-10-13',
    note: 'OAR 333-265-0110(4)-(8); OAR 333-265-0045 (eff. July 1, 2020): "(a) Complete all requirements of the National Registry for Paramedic recertification; or (b) Obtain at least 48 hours of continuing education"',
  },
  PA: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.pacodeandbulletin.gov/Display/pacode?file=/secure/pacode/data/028/chapter1023/s1023.31.html',
    checked: '2026-10-13',
    note: '28 Pa. Code 1023.31 (site current through 56 Pa.B. 5370, Aug 8, 2026): "an EMT whose certification is currently registered and who elects to qualify for triennial registration of the certification by fulfilling continuing education requirements"',
  },
  RI: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: true,
    source: 'https://rules.sos.ri.gov/Regulations/part/216-20-10-2',
    checked: '2026-10-13',
    note: '216-RICR-20-10-2, section 2.5(C)(2)-(6) (effective 12/27/2020, shown as current): "An EMT licensed after January 1, 2012 must maintain NREMT certification as an EMT."',
  },
  SD: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://sdlegislature.gov/api/Rules/20:61:03:02.html',
    checked: '2026-10-13',
    note: 'ARSD 20:61:03:02 (51 SDR 56, eff. Nov 19, 2024); CE hours in 20:61:03:01: "submit a signed statement confirming the applicant\'s current certification by the National Registry of Emergency Medical Technicians or completion of the continuing education hours"',
  },
  TN: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.tn.gov/content/dam/tn/health/events/718%20Renewal%20BckPg%20Rev%20Jan2024%20for%20WEBSITE.pdf',
    checked: '2026-10-13',
    note: 'Tenn. Comp. R. & Regs. 1200-12-01-.04 (official SOS PDF returned 403; not opened): "Successful completion of ONE of the following: Continuing Education Hours (CEH\'s) Acceptable College Credit Hours Successful completion of the National Registry Assessment Examination"',
  },
  VA: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.vdh.virginia.gov/emergency-medical-services/education-certification/provider-resources/recertifying-your-virginia-ems-credential/virginia-recertification/',
    checked: '2026-10-13',
    note: '12VAC5-31-1401(A) (CE hours per 12VAC5-31-1403): "Virginia does not require providers to maintain their National Registry certification."',
  },
  VT: {
    initialWithoutNremt: { emt: false, paramedic: false },
    renewalRequiresNremt: true,
    source: 'https://www.healthvermont.gov/sites/default/files/document/reg-emergency-medical-services.pdf',
    checked: '2026-10-13',
    note: 'Vermont EMS Rule sections 8.3.6, 8.3.9.2, 8.3.3.6 (eff. Feb 1, 2022): "Persons who hold or have ever held NREMT certification must maintain that certification for the duration of their Vermont EMS licensure."',
  },
  WA: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://app.leg.wa.gov/WAC/default.aspx?cite=246-976-161',
    checked: '2026-10-13',
    note: 'WAC 246-976-161(1); WAC 246-976-171(3)(a): "There are two methods by which the EMS provider may meet continuing medical education and ongoing training and skill requirements for recertification"',
  },
  WI: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://www.dhs.wisconsin.gov/ems/training/education-renewal-23-26.htm',
    checked: '2026-10-13',
    note: 'Wis. Admin. Code DHS 110.07(1)(c) (CR 20-028, eff. Oct 1, 2021): "You have current NREMT certification that is valid on the first day of the renewal triennium (July 1, 2023)."',
  },
  WV: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: true,
    source: 'https://dhhr.wv.gov/oems/resources/Documents/Provider%20Licensure/EMT%20Relicensure%20Policy%202025.pdf',
    checked: '2026-10-13',
    note: 'WVOEMS Relicensure Policies (April 2025), citing WV Code 16-4C-6, 16-4C-8.1 and 64 CSR 48-6 (rule text not opened): "must be continually certified by the National Registry of Emergency Medical Technicians (NREMT) during the duration of your WVOEMS licensure"',
  },
  WY: {
    initialWithoutNremt: { emt: null, paramedic: null },
    renewalRequiresNremt: false,
    source: 'https://health.wyo.gov/wp-content/uploads/2020/06/Chapter-18-Personnel-Licensure-Requirements.pdf',
    checked: '2026-10-13',
    note: 'Wyoming Dept. of Health EMS Rules, Chapter 18, Section 2(a)(iii) (file posted June 2020): "(A) Has completed the required hours of CME course work specific to the licensee\'s level of licensure, as established under this Chapter; or"',
  },
};

/**
 * What is CONFIRMED about a state's rule at a level: a current NREMT is 'required' to keep the license,
 * 'not_required', or 'unconfirmed' (state not listed, or the level not confirmed).
 */
export function stateNremtRuleStatus(state: string | null | undefined, level?: string | null): 'required' | 'not_required' | 'unconfirmed' {
  const code = normalizeStateCode(state);
  const rule = code ? STATE_NREMT_RULES[code] : undefined;
  if (!rule) return 'unconfirmed';
  const lvl = (normalizeStateCertificationLevel(level) ?? (typeof level === 'string' && /^emr$/i.test(level.trim()) ? 'EMR' : 'EMT-B')) as EmsRuleLevel;
  const levels = rule.renewalRequiresNremtForLevels;
  const override = levels && lvl in levels ? levels[lvl] : undefined;
  if (override === null) return 'unconfirmed';
  return (override ?? rule.renewalRequiresNremt) ? 'required' : 'not_required';
}

/**
 * True when a state certification at `level` stands in for NREMT: everywhere except where a current
 * NREMT is CONFIRMED as required to keep the license (2026-10-14: an unconfirmed state no longer costs
 * an EMT points; before, it was treated like a "required" state).
 */
export function stateKeepsLicenseWithoutNremt(state: string | null | undefined, level?: string | null): boolean {
  if (!normalizeStateCode(state)) return false;
  return stateNremtRuleStatus(state, level) !== 'required';
}

/** The confirmed answer at the EMT level, read straight from the table (safe at module load). */
function emtLevelStatus(code: string): 'required' | 'not_required' | 'unconfirmed' {
  const rule = STATE_NREMT_RULES[code];
  const o = rule.renewalRequiresNremtForLevels?.['EMT-B'];
  if (o === null) return 'unconfirmed';
  return (o ?? rule.renewalRequiresNremt) ? 'required' : 'not_required';
}

/** States CONFIRMED (at the EMT level) not to require a current NREMT to keep the license. */
export const STATES_CERTIFYING_WITHOUT_NREMT: readonly string[] = Object.keys(STATE_NREMT_RULES).filter((s) => emtLevelStatus(s) === 'not_required');

/** States CONFIRMED (at the EMT level) to require a current NREMT to keep the license. */
export const STATES_REQUIRING_NREMT: readonly string[] = Object.keys(STATE_NREMT_RULES).filter((s) => emtLevelStatus(s) === 'required');

/**
 * The one sentence shown when a state certification stands in for NREMT:
 * "Florida EMT certification on file. Florida doesn't require a current NREMT to keep it."
 * Never "Florida doesn't require NREMT" (new Florida licenses do need the exam).
 */
export function stateCertificationNote(state: string | null | undefined, level?: string | null): string | null {
  const code = normalizeStateCode(state);
  if (!code || !stateKeepsLicenseWithoutNremt(code, level)) return null;
  const name = US_STATE_NAMES[code] ?? code;
  const lvl = normalizeStateCertificationLevel(level) ?? 'EMT-B';
  const levelWord = lvl === 'EMT-B' ? 'EMT' : lvl === 'AEMT' ? 'Advanced EMT' : 'Paramedic';
  // The fact about the state only where it is confirmed on an official page; otherwise Arevina's own rule.
  return stateNremtRuleStatus(code, level) === 'not_required'
    ? `${name} ${levelWord} certification on file. ${name} doesn't require a current NREMT to keep it.`
    : `${name} ${levelWord} certification on file. It counts in place of NREMT for jobs in ${name}.`;
}

/** Names for the states a rule can name (kept here so this file stays dependency-free). */
const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

/** Where a job is and whether the employer requires National Registry certification. */
export type CertificationJobContext = {
  /** The job's 2-letter state code, when known. */
  jobState?: string | null;
  /** certificationRequirements.nremtRequired on the job (absent = false). */
  nremtRequired?: boolean;
  /** certificationRequirements.stateLicenseRequired: "Must hold our state's license (no Compact privilege)". */
  stateLicenseRequired?: boolean;
};

/**
 * EMS Compact (REPLICA) states, from the Compact's own page (2026-10-10):
 * https://www.emscompact.gov/the-compact/participating-states/ (the old /member-states link redirects).
 *  - EMS_COMPACT_HOME_STATES: "Enacted and fully implemented. The Compact is in force and the Privilege
 *    to Practice is recognized here." A license from one of these is a Compact home-state license.
 *  - EMS_COMPACT_PRIVILEGE_STATES: those 25 plus Oklahoma and Pennsylvania ("Enacted and in force, with
 *    database integration underway. The Privilege to Practice is recognized here."). Connecticut is
 *    enacted but not yet operational (privilege NOT recognized), so it is in neither list.
 * Non-members (for example FL, NC, CA, NY, IL, MI, MT): no Compact privilege, matching unchanged.
 */
export const EMS_COMPACT_SOURCE = 'https://www.emscompact.gov/the-compact/participating-states/';
export const EMS_COMPACT_CHECKED = '2026-10-10';
export const EMS_COMPACT_HOME_STATES: readonly string[] = [
  'AL', 'AK', 'AZ', 'AR', 'CO', 'DE', 'GA', 'ID', 'IN', 'IA', 'KS', 'LA', 'MS', 'MO', 'NE', 'NV', 'ND', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'WV', 'WY',
];
export const EMS_COMPACT_PRIVILEGE_STATES: readonly string[] = [...EMS_COMPACT_HOME_STATES, 'OK', 'PA'];
export const COMPACT_PRIVILEGE_LABEL = 'Compact privilege to practice (EMS Compact)';

/**
 * True when a State EMS Certification from `heldState` meets a job's required State EMS Certification
 * through the EMS Compact: the job is in a state that recognizes the privilege, the license is from a
 * different Compact home state, and the employer did not tick "Must hold our state's license".
 * (The Compact covers an active, unrestricted license; restrictions are not on Arevina profiles, so the
 * employer still checks the license with "Check this license".)
 */
export function compactPrivilegeApplies(heldState: unknown, ctx?: CertificationJobContext | null): boolean {
  const held = normalizeStateCode(heldState);
  const job = normalizeStateCode(ctx?.jobState);
  if (!held || !job || held === job || ctx?.stateLicenseRequired === true) return false;
  return EMS_COMPACT_HOME_STATES.includes(held) && EMS_COMPACT_PRIVILEGE_STATES.includes(job);
}

const NREMT_LEVEL_NAMES: Record<string, string> = {
  'match:emt-b': 'EMT-B',
  'match:aemt': 'AEMT',
  'match:paramedic': 'Paramedic',
};

/** Levels a State EMS Certification can be recorded at (the scope it certifies). */
export const STATE_CERTIFICATION_LEVELS = ['EMT-B', 'AEMT', 'Paramedic'] as const;
export type StateCertificationLevel = (typeof STATE_CERTIFICATION_LEVELS)[number];

/** "EMT", "EMT-Basic", "NREMT-P", "Paramedic"... to a State EMS Certification level, or null. */
export function normalizeStateCertificationLevel(raw: unknown): StateCertificationLevel | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const t = raw.trim();
  const key = /^emt$/i.test(t) ? 'match:emt-b' : certificationMatchKey(t);
  return (NREMT_LEVEL_NAMES[key] as StateCertificationLevel | undefined) ?? null;
}

/**
 * True when a State EMS Certification for the job's state can stand in for an NREMT-level requirement:
 * the job's state does not require a current NREMT to keep a license at `heldLevel` (STATE_NREMT_RULES;
 * no level = EMT-B), and the employer did not tick "National Registry (NREMT) certification required".
 */
export function stateCertificationMeetsNremtLevel(ctx?: CertificationJobContext | null, heldLevel?: string | null): boolean {
  const state = normalizeStateCode(ctx?.jobState);
  return Boolean(state && stateKeepsLicenseWithoutNremt(state, heldLevel) && ctx?.nremtRequired !== true);
}

/** A held state matches the job's state; a missing state on either side does not block (fail-open). */
function stateCertificationStateMatches(heldState: unknown, jobState: unknown): boolean {
  const held = normalizeStateCode(heldState);
  const job = normalizeStateCode(jobState);
  return !held || !job || held === job;
}

/**
 * certificationSatisfies with the job's context: the one rule behind job matching, the
 * "Documents Reviewed" apply gate (client and server) and the hiring checklist.
 *  - Required "State EMS Certification": met only by a State EMS Certification whose state equals
 *    the job's state, or whose state is missing, or when the job has no state (fail-open).
 *  - Job in a state whose license is kept without a current NREMT (STATE_NREMT_RULES) and NREMT not
 *    marked required: a required NREMT-B/I/P
 *    (or EMT-B/AEMT/Paramedic) is also met by a State EMS Certification for that state (or one with
 *    no state recorded) AT THAT LEVEL OR HIGHER. A state certification with no level recorded counts
 *    as EMT-B only: it never opens an AEMT or Paramedic job (2026-10-09).
 *  - Everything else: exactly certificationSatisfies. With no context this IS certificationSatisfies.
 */
export function certificationSatisfiesForJob(
  heldName: string,
  heldState: string | null | undefined,
  required: string,
  ctx?: CertificationJobContext | null,
  heldLevel?: string | null,
): boolean {
  if (!heldName?.trim() || !required?.trim()) return false;
  const heldKey = certificationMatchKey(heldName);
  const reqKey = certificationMatchKey(required);
  const stateKey = certificationMatchKey(STATE_EMS_CERTIFICATION);
  if (reqKey === stateKey) {
    // Its own state (or a state missing on either side), or another Compact state's license (EMS Compact).
    return heldKey === stateKey && (stateCertificationStateMatches(heldState, ctx?.jobState) || compactPrivilegeApplies(heldState, ctx));
  }
  if (certificationSatisfies(heldName, required)) return true;
  // A required EMR is on the ladder too: a state certification (EMT-B or higher by construction)
  // meets it under the same state rule (STATE_NREMT_RULES), never anywhere else.
  return (
    heldKey === stateKey &&
    (NREMT_LEVEL_NAMES[reqKey] !== undefined || reqKey === CERT_MATCH_GROUPS.EMR) &&
    stateCertificationMeetsNremtLevel(ctx, heldLevel) &&
    stateCertificationStateMatches(heldState, ctx?.jobState) &&
    (CERT_LEVEL_RANK[certificationMatchKey(normalizeStateCertificationLevel(heldLevel) ?? 'EMT-B')] ?? CERT_LEVEL_RANK['match:emt-b']) >= (CERT_LEVEL_RANK[reqKey] ?? 99)
  );
}

/** A profile certification as matching needs it: name plus the issuing state, when recorded. */
export type HeldCertification = { name: string; state?: string | null; level?: string | null };

/** profileHasCertification with each entry's state and the job's context. */
export function profileHasCertificationForJob(
  held: readonly HeldCertification[],
  required: string,
  ctx?: CertificationJobContext | null,
): boolean {
  if (!certificationMatchKey(required)) return false;
  return held.some((h) => certificationSatisfiesForJob(h.name, h.state, required, ctx, h.level));
}

/**
 * How a required credential is named to candidates for this job. In a state listed in
 * STATE_NREMT_RULES that keeps licenses without a current NREMT an NREMT-level requirement names the level:
 * "EMT-B (state certification or NREMT)", never "NREMT-B". A State EMS Certification requirement
 * names the job's state. Otherwise the canonical display name.
 */
export function describeCertificationRequirement(required: string, ctx?: CertificationJobContext | null): string {
  const key = certificationMatchKey(required);
  const level = NREMT_LEVEL_NAMES[key];
  if (level && stateCertificationMeetsNremtLevel(ctx)) return `${level} (state certification or NREMT)`;
  if (key === certificationMatchKey(STATE_EMS_CERTIFICATION)) {
    return formatCertificationName(STATE_EMS_CERTIFICATION, ctx?.jobState);
  }
  return displayCertificationName(required);
}

/** Parse YYYY-MM-DD as a local calendar date (no UTC midnight shift). */
export function parseDateOnlyLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return new Date(y, mo - 1, d);
}

/** Format a YYYY-MM-DD string for UI without timezone drift. */
export function formatDateOnlyLocal(
  ymd: string,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = parseDateOnlyLocal(ymd);
  if (!d) return ymd.trim();
  return d.toLocaleDateString(locales, options);
}

/** Calendar YYYY-MM-DD in the local timezone. */
export function toDateOnlyLocal(from: Date = new Date()): string {
  const y = from.getFullYear();
  const m = String(from.getMonth() + 1).padStart(2, '0');
  const day = String(from.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Hint text for an expiration-date field: the credential's usual renewal cycle, or null for unknown /
 * custom certs. Deliberately NOT a date — expiry fields are never pre-filled with a computed guess,
 * which a candidate could submit unread as their credential's real expiry.
 */
export function certificationExpiryHint(certName: string): string | null {
  const canonical = resolveCanonicalCertification(certName);
  if (!canonical) return null;
  const years = CERT_VALIDITY_YEARS[canonical];
  if (years == null) return null;
  return `${canonical} is usually renewed every ${years} year${years === 1 ? '' : 's'}: enter the expiration date printed on yours.`;
}

export type HowToObtainCert = {
  url: string;
  sourceLabel: string;
};

/**
 * State certification is issued by each state's own EMS office, so the pointer is neutral: the
 * NREMT state map links every state's EMS office (and says to contact the office of the state you
 * want to work in). No single state is hard-coded.
 */
const STATE_EMS_OFFICE_POINTER: HowToObtainCert = {
  url: 'https://www.nremt.org/rwd/public/states/state-ems-agencies',
  sourceLabel: 'your state EMS office',
};

/**
 * External “how to get this” links for certification gaps.
 * Prefer certification-specific pages (not bare domain homepages).
 * CAPCE: deeper paths currently 404 — keep the verified root.
 */
export function getHowToObtainCert(certName: string): HowToObtainCert {
  const key = normalizeCertKey(certName);
  const canonical = resolveCanonicalCertification(certName);
  const label = (canonical ?? certName).toUpperCase();

  switch (canonical) {
    case 'EMR':
      return { url: 'https://www.nremt.org/EMR/Certification', sourceLabel: 'NREMT' };
    case 'NREMT-B':
    case 'EMT-B':
      return { url: 'https://www.nremt.org/EMT/Certification', sourceLabel: 'NREMT' };
    // NREMT retired the "EMT-Intermediate" / "NREMT-I" name years ago — AEMT is the modern
    // exam that now covers it, so there's no separate NREMT-I-specific page to link to. But
    // NREMT-I and AEMT are independently selectable on this profile (a candidate can hold one
    // without the other), so silently sending a "NREMT-I" gap to a page titled "AEMT
    // Certification" with an unqualified "NREMT" label reads as a wrong/mismatched link. Label
    // it explicitly instead of rebranding quietly.
    case 'NREMT-I':
      return {
        url: 'https://www.nremt.org/AEMT/Certification',
        sourceLabel: 'NREMT AEMT certification',
      };
    case 'AEMT':
      return { url: 'https://www.nremt.org/AEMT/Certification', sourceLabel: 'NREMT' };
    // Same reasoning as NREMT-I/AEMT above: NREMT-P and Paramedic are independently
    // selectable, and "Paramedic" is the modern name for the NREMT-P exam.
    case 'NREMT-P':
      return {
        url: 'https://www.nremt.org/Paramedic/Certification',
        sourceLabel: 'NREMT Paramedic certification',
      };
    case 'Paramedic':
      return { url: 'https://www.nremt.org/Paramedic/Certification', sourceLabel: 'NREMT' };
    case 'EVOC':
      // No single national certifying body — EVOC is delivered by state/regional EMS
      // systems and employers. Link to NHTSA's national standard reference curriculum.
      return {
        url: 'https://naemt.org/education/operational-education/ems-vehicle-operator-safety/evos-courses',
        sourceLabel: 'NAEMT EVOS (one accepted course; ask the employer which one they want)',
      };
    case 'California Ambulance Driver Certificate':
      return {
        url: 'https://www.dmv.ca.gov/portal/driver-education-and-safety/dmv-safety-guidelines-actions/driver-license-certificates-and-endorsements/',
        sourceLabel: 'California DMV',
      };
    case 'State EMS Certification':
      return STATE_EMS_OFFICE_POINTER;
    case 'BLS':
      return {
        url: 'https://cpr.heart.org/en/cpr-courses-and-kits/healthcare-professional/basic-life-support-bls-training',
        sourceLabel: 'AHA BLS',
      };
    case 'ACLS':
      return {
        url: 'https://cpr.heart.org/en/cpr-courses-and-kits/healthcare-professional/acls',
        sourceLabel: 'AHA ACLS',
      };
    case 'PALS':
      return {
        url: 'https://cpr.heart.org/en/cpr-courses-and-kits/healthcare-professional/pediatric',
        sourceLabel: 'AHA PALS',
      };
    case 'Critical Care Paramedic Certificate':
      return {
        url: 'https://www.ibscertifications.org/roles/critical-care-paramedic',
        sourceLabel: 'IBSC CCP-C',
      };
    case 'Wilderness First Responder':
      return {
        url: 'https://www.nols.edu/courses/wilderness-first-responder-wfr',
        sourceLabel: 'NOLS Wilderness First Responder',
      };
    case 'Wilderness EMT':
      return {
        url: 'https://www.nols.edu/courses/wilderness-upgrade-for-medical-professionals-WUMP/',
        sourceLabel: 'NOLS Wilderness Upgrade for Medical Professionals',
      };
    default:
      break;
  }

  // Non-canonical aliases that still look like NREMT levels
  if (key.includes('nremt') || /^emt-?[bip]$/.test(key)) {
    if (key.includes('paramedic') || key.includes('nremt-p') || /emt-?p$/.test(key)) {
      return {
        url: 'https://www.nremt.org/Paramedic/Certification',
        sourceLabel: key.includes('nremt-p') ? 'NREMT Paramedic certification' : 'NREMT',
      };
    }
    if (key.includes('aemt') || key.includes('nremt-i') || key.includes('nremt-a') || /emt-?i$/.test(key)) {
      return {
        url: 'https://www.nremt.org/AEMT/Certification',
        sourceLabel: key.includes('nremt-i') || key.includes('nremt-a')
          ? 'NREMT AEMT certification'
          : 'NREMT',
      };
    }
    return { url: 'https://www.nremt.org/EMT/Certification', sourceLabel: 'NREMT' };
  }

  if (key.includes('evoc') || key.includes('emergency-vehicle')) {
    return {
      url: 'https://naemt.org/education/operational-education/ems-vehicle-operator-safety/evos-courses',
      sourceLabel: 'NAEMT EVOS (one accepted course; ask the employer which one they want)',
    };
  }

  if (key.includes('acls')) {
    return {
      url: 'https://cpr.heart.org/en/cpr-courses-and-kits/healthcare-professional/acls',
      sourceLabel: 'AHA ACLS',
    };
  }
  if (key.includes('pals')) {
    return {
      url: 'https://cpr.heart.org/en/cpr-courses-and-kits/healthcare-professional/pediatric',
      sourceLabel: 'AHA PALS',
    };
  }
  if (key.includes('bls') || key.includes('cpr')) {
    return {
      url: 'https://cpr.heart.org/en/cpr-courses-and-kits/healthcare-professional/basic-life-support-bls-training',
      sourceLabel: 'AHA BLS',
    };
  }

  if (key.includes('license') || key.includes('licensure') || key.includes('state-ems')) {
    return STATE_EMS_OFFICE_POINTER;
  }
  // CEU / course-style requirements and other unknowns: CAPCE's course search (opened 2026-10-10).
  return { url: 'https://www.capce.org/Home/FindCourse', sourceLabel: label.includes('CAPCE') ? 'CAPCE' : 'CAPCE course search' };
}

/** Build /account deep link; URLSearchParams encodes cert names (hyphens, spaces, etc.). */
export function buildStrengthenAccountPath(missingCerts: string[], jobId?: string): string {
  const params = new URLSearchParams();
  params.set('strengthen', missingCerts.join(','));
  if (jobId) params.set('fromJob', jobId);
  return `/account?${params.toString()}#certifications`;
}

/** Parse ?strengthen= after URLSearchParams decode (comma-separated cert names). */
export function parseStrengthenQuery(raw: string | null): string[] {
  if (!raw || !raw.trim()) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
